import { parsePhoneNumber } from 'libphonenumber-js';
import { scrollAnimate, getFieldsFromDynamicStructure } from '@/components/helper/util';

export default {
    state: {
        formFields: {
            shipping: {},
            billing: {},
            login: {}
        },
        fieldErrors: {
            shipping: {},
            billing: {},
            login: {}
        },
        shippingDynamicStructure: {},
        billingDynamicStructure: {},
        pickupDynamicStructure: {},
        billingLocale: {},
        shippingLocale: {},
        pickupLocale: {},
        lastFormData: {},
        validatedFields: {
            shipping: {},
            billing: {}
        }
    },
    getters: {
        formFields(state) {
            return state.formFields;
        },
        fieldErrors(state) {
            return state.fieldErrors;
        },
        validatedFields(state) {
            return state.validatedFields;
        },
        shippingDynamicStructure(state) {
            return state.shippingDynamicStructure;
        },
        billingDynamicStructure(state) {
            return state.billingDynamicStructure;
        },
        pickupDynamicStructure(state) {
            return state.pickupDynamicStructure;
        },
        billingLocale(state) {
            return state.billingLocale;
        },
        shippingLocale(state) {
            return state.shippingLocale;
        },
        pickupLocale(state) {
            return state.pickupLocale;
        },
        lastFormData(state) {
            return state.lastFormData;
        }
    },
    mutations: {
        setBillingLocale(state, data) {
            state.billingLocale = data;
        },
        setShippingLocale(state, data) {
            state.shippingLocale = data;
        },
        setPickupLocale(state, data) {
            state.pickupLocale = data;
        },
        setShippingDynamicStructure(state, data) {
            state.shippingDynamicStructure[data.locale] = data.value;
        },
        setBillingDynamicStructure(state, data) {
            state.billingDynamicStructure[data.locale] = data.value;
        },
        setPickupDynamicStructure(state, data) {
            state.pickupDynamicStructure[data.locale] = data.value;
        },
        setLastFormData(state, data) {
            state.lastFormData = data;
        },
        clearFieldErrors(state, data) {
            state.fieldErrors[data.formName] = {};
        },
        clearFieldError(state, data) {
            if (state.fieldErrors[data.formName] && state.fieldErrors[data.formName][data.fieldName]) {
                delete state.fieldErrors[data.formName][data.fieldName];
            }
        },
        updateValidatedFields(state, data) {
            state.validatedFields[data.formName][data.fieldName] = data.fieldValue;
        },
        updateFieldError(state, data) {
            state.fieldErrors[data.formName][data.fieldName] = data.fieldValue;
        },
        updateFormField(state, data) {
            state.formFields[data.formName][data.fieldName] = data.fieldValue;
        },
        setFieldError(state, data) {
            state.fieldErrors[data.form][data.fieldId] = data.value;
        }
    },
    actions: {
        handleDataAddressModeOnFieldErrors({ state, commit, rootState }) {
            // Handle DataAddressMode
            if (rootState.checkout && (rootState.checkout.shippingVars.dataAddressMode || rootState.checkout.billingVars.dataAddressMode)) {
                switch (rootState.checkout.currentStage) {
                    case 'shipping':
                        if (rootState.checkout.shippingVars.dataAddressMode === 'customer') {
                            commit('setShippingVars', { fieldName: 'dataAddressMode', fieldValue: 'details' }, { root: true });
                        }
                        break;
                    case 'payment':
                        if (rootState.checkout.billingVars.dataAddressMode === 'customer') {
                            const billingKeys = Object.keys(state.fieldErrors.billing).concat(Object.keys(state.formFields.billing));
                            const uniqueBillingKeys = [...new Set(billingKeys)];
                            if (billingKeys.length !== uniqueBillingKeys.length) {
                                commit('setBillingVars', { fieldName: 'dataAddressMode', fieldValue: 'details' }, { root: true });
                            }
                        }
                        break;
                    default:
                        break;
                }
            }
        },

        handleFieldErrors({
            state, commit, rootState, dispatch
        }, fieldErrors) {
            // Handle Each Field
            const addressFields = rootState.checkout && rootState.checkout.currentStage === 'shipping' ? state.lastFormData.shipping : state.lastFormData.billing;
            fieldErrors.forEach((errors) => {
                Object.entries(errors).forEach(([key, value]) => {
                    // eslint-disable-next-line no-shadow
                    const field = Object.values(addressFields).find((field) => field.htmlName === key);
                    commit('updateFieldError', {
                        formName: rootState.checkout && rootState.checkout.currentStage === 'shipping' ? 'shipping' : 'billing',
                        fieldName: field.id,
                        fieldValue: value
                    });
                });
            });
            dispatch('handleDataAddressModeOnFieldErrors');
        },

        // eslint-disable-next-line no-unused-vars
        validateAndParse({ state, commit, rootState }, { formType, formFields }) {
            const translations = window.vue.translations;

            const onValidationError = () => {
                commit('updateFieldError', { formName: formType, fieldName: 'phone', fieldValue: translations['error.message.parse.phone.profile.form'] });
                commit('updateValidatedFields', { formName: formType, fieldName: 'phone', fieldValue: { valid: false } });
            };

            const phoneCountryCodeMap = rootState.checkout ? rootState.checkout.checkout.phoneCountryCodeMap : rootState.pdict.phoneCountryCodeMap;

            const currentAreaCode = formFields.country === 'HK' ? formFields.postalCode : formFields.areaCode;
            const currentCountry = currentAreaCode ? phoneCountryCodeMap[currentAreaCode] : (formFields.countryCode || formFields.country);

            let error = false;
            let phoneNumberParsed = null;
            try {
                phoneNumberParsed = parsePhoneNumber(formFields.phone, currentCountry);
            } catch (e) {
                error = true;
            }
            if (error) {
                onValidationError();
            } else {
                let acceptableCountry = currentCountry === phoneNumberParsed.country;

                if (currentCountry === 'CA' && phoneNumberParsed.country === 'US') {
                    acceptableCountry = true;
                }

                if (!phoneNumberParsed.isValid() || !acceptableCountry) {
                    onValidationError();
                } else {
                    commit('updateValidatedFields', {
                        formName: formType,
                        fieldName: 'phone',
                        fieldValue: {
                            valid: true,
                            nationalNumber: phoneNumberParsed.nationalNumber,
                            nationalFormatted: phoneNumberParsed.formatNational()
                        }
                    });
                }
            }
        },

        updateFormField({ commit }, payload) {
            commit('updateFormField', payload);
            commit('clearFieldError', payload);
        },

        // eslint-disable-next-line no-unused-vars
        handleClientSideFieldError({ state, commit, rootState }, { formName, field, t }) {
            const fieldElement = document.getElementById(field.formId);

            if (fieldElement.checkValidity()) {
                return;
            }

            let errorMessage = '';

            const locale = formName === 'shipping' ? state.shippingLocale : state.billingLocale;

            if (fieldElement.validity.valueMissing) {
                let missingError = null;
                if (field.missingError && typeof field.missingError === 'object') {
                    missingError = field.missingError[locale];
                } else if (field.missingError && typeof field.missingError === 'string') {
                    missingError = field.missingError;
                }
                errorMessage = missingError || (t(`${field.id}.missing`) !== `${field.id}.missing` ? t(`${field.id}.missing`) : t('error.message.required'));
            } else if (fieldElement.validity.tooLong || fieldElement.validity.tooShort || fieldElement.validity.rangeOverflow || fieldElement.validity.rangeUnderflow) {
                let rangeError = null;
                if (field.rangeError && typeof field.rangeError === 'object') {
                    rangeError = field.rangeError[locale];
                } else if (field.rangeError && typeof field.rangeError === 'string') {
                    rangeError = field.rangeError;
                }
                errorMessage = rangeError || t('error.message.between', [field.minLength, field.maxLength]);
            } else if (fieldElement.validity.patternMismatch && field.patternError) {
                let patternError = null;
                if (typeof field.patternError === 'object') {
                    patternError = field.patternError[locale];
                } else if (typeof field.patternError === 'string') {
                    patternError = field.patternError;
                }
                errorMessage = patternError;
            } else if (field.id === 'postalCode') {
                errorMessage = t('error.message.parse.zip');
            } else if (field.id === 'email' && field.regEx && !(new RegExp(field.regEx)).test(fieldElement.value)) {
                errorMessage = t('error.message.parse.email.profile.form');
            } else if (field.id !== 'phone' && field.regEx && !(new RegExp(field.regEx)).test(fieldElement.value)) {
                errorMessage = t('error.message.latinletters');
            }
            if (errorMessage) {
                commit('updateFieldError', {
                    formName,
                    fieldName: field.id,
                    fieldValue: errorMessage
                });
            }
        },

        async handleClientSideFieldErrors({
            state, commit, rootState, dispatch
        }, { formName, t, specificFields }) {
            const isSameAsShipping = document.getElementById('sameasshipping') ? document.getElementById('sameasshipping').checked : null;

            commit('clearFieldErrors', { formName });

            let addressFields = {};
            if (formName === 'shipping' || formName === 'billing' && !isSameAsShipping) {
                const currentFormFieldStructure = formName === 'shipping' ? (rootState.checkout && rootState.checkout.shippingVars.isPickupMethodSelected ? state.pickupDynamicStructure[state.shippingLocale] : state.shippingDynamicStructure[state.shippingLocale]) : state.billingDynamicStructure[state.billingLocale];
                addressFields = getFieldsFromDynamicStructure(currentFormFieldStructure);
                Object.values(addressFields).forEach((field) => {
                    if ((!specificFields || specificFields.includes(field.name)) && field.visible !== false && !['inject', 'hidden'].includes(field.type)) {
                        dispatch('handleClientSideFieldError', { formName, field, t });
                    }
                    if (field.visible !== false && field.formId !== 'pickupEmail' && field.name === 'email' && formName === 'shipping' && rootState.vueVariables && rootState.vueVariables.isCustomerAuthenticated) {
                        dispatch('handleClientSideFieldError', { formName, field, t });
                    }
                });
            }
            if (formName === 'login') {
                Object.values(specificFields).forEach((field) => {
                    addressFields[field.name] = field;
                    dispatch('handleClientSideFieldError', { formName, field, t });
                });
            }

            const fieldErrorKeys = Object.keys(state.fieldErrors[formName]);

            if (fieldErrorKeys.length > 0) {
                const firstErrorField = Object.values(addressFields).find((field) => field.name === fieldErrorKeys[0]);
                setTimeout(() => {
                    scrollAnimate(document.querySelector(`#${firstErrorField.formId}`));
                }, 10);
                dispatch('handleDataAddressModeOnFieldErrors');
                return false;
            }

            return true;
        }

    }
};
